import { useEffect, useRef, useState } from "react";

import {
  useLazyGetDeviceCommandStatusQuery,
  useSendDeviceCommandMutation,
} from "@/features/devices/devicesApi";
import {
  DeviceDetailResponse,
  detailMatchesDevice,
} from "@/features/devices/deviceDetailDisplay";
import { waitForDeviceInfoReport } from "@/features/devices/deviceInfoOnDemandReport";
import {
  REPORT_DEVICE_INFO_COMMAND,
  canRequestReportDeviceInfo,
  shouldSendReportOnExpand,
} from "@/features/devices/deviceInfoReportSupport";

export type DeviceInfoReportUiState =
  | "idle"
  | "sending"
  | "successful"
  | "unsuccessful"
  | "skipped_unsupported"
  | "skipped_no_evidence";

/**
 * One-shot report_device_info on Device Info expand.
 *
 * Expand-only Option B live polling flips isFetching on a 4s cadence.
 * Those poll cycles must not abort an in-flight report or leave
 * "Updating from device…" stuck at sending — only collapse / deviceId
 * change sets stopRef.
 */
export function useDeviceInfoOnDemandReport(args: {
  expanded: boolean;
  deviceId?: string | null;
  detailResponse?: DeviceDetailResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch?: () => Promise<unknown>;
}): { reportState: DeviceInfoReportUiState } {
  const {
    expanded,
    deviceId,
    detailResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = args;

  const [sendDeviceCommand] = useSendDeviceCommandMutation();
  const [getCommandStatus] = useLazyGetDeviceCommandStatusQuery();
  const [reportState, setReportState] =
    useState<DeviceInfoReportUiState>("idle");

  const cacheMissRef = useRef(false);
  const sentRef = useRef(false);
  const stopRef = useRef(true);
  const detailRef = useRef(detailResponse);
  detailRef.current = detailResponse;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Collapse / device change: abort and reset. Live poll isFetching must not.
  useEffect(() => {
    if (!expanded) {
      cacheMissRef.current = false;
      sentRef.current = false;
      stopRef.current = true;
      setReportState("idle");
      return;
    }
    stopRef.current = false;
    return () => {
      stopRef.current = true;
    };
  }, [expanded, deviceId]);

  useEffect(() => {
    if (expanded && isLoading) cacheMissRef.current = true;
  }, [expanded, isLoading]);

  useEffect(() => {
    if (!expanded || !deviceId || isError) return;
    // Already claimed (in flight or finished) — poll isFetching flips must
    // re-enter here without aborting the async work below.
    if (sentRef.current) return;
    if (isLoading || isFetching) return;

    const detail = detailRef.current;
    if (!detail || !detailMatchesDevice(detail.deviceId, deviceId)) {
      return;
    }

    const capability = canRequestReportDeviceInfo({
      supportsReportDeviceInfo: detail.supportsReportDeviceInfo,
      capabilities: detail.capabilities,
      commandSupport: detail.commandSupport,
      playerVersion: detail.detail?.player?.version,
    });

    if (
      !shouldSendReportOnExpand({
        expanded,
        deviceId,
        cacheMiss: cacheMissRef.current,
        authorized: !isError,
        capability,
      })
    ) {
      sentRef.current = true;
      if (!cacheMissRef.current) {
        setReportState("idle");
        return;
      }
      setReportState(
        capability.reason === "unsupported"
          ? "skipped_unsupported"
          : "skipped_no_evidence"
      );
      return;
    }

    sentRef.current = true;
    setReportState("sending");

    (async () => {
      try {
        const sent = await sendDeviceCommand({
          deviceId,
          command: REPORT_DEVICE_INFO_COMMAND,
        }).unwrap();
        if (stopRef.current) return;
        const outcome = await waitForDeviceInfoReport({
          commandId: sent.commandId,
          getStatus: async (id) => {
            const res = await getCommandStatus({ commandId: id }).unwrap();
            return res.command;
          },
          shouldStop: () => stopRef.current,
        });
        if (stopRef.current || outcome === "cancelled") return;
        setReportState(outcome);
        if (outcome === "successful" && refetchRef.current) {
          await refetchRef.current();
        }
      } catch {
        if (!stopRef.current) setReportState("unsuccessful");
      }
    })();

    // No effect cleanup abort: isFetching poll cycles re-run this effect.
    // Abort is solely via stopRef from the expand/deviceId effect.
  }, [
    expanded,
    deviceId,
    isLoading,
    isFetching,
    isError,
    detailResponse?.deviceId,
    sendDeviceCommand,
    getCommandStatus,
  ]);

  return { reportState };
}
